import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Col, message, Row, Typography } from 'antd';

import { Wizard } from '@hdi-seguros/web-components.wizard';
import { ActionButtonProps } from '@hdi-seguros/web-components.action-button';

import { PageHeader } from '../../../shared/components/MainLayout/components';
import { GenericDataFormContext, GenericDataFormProvider, LOADING_SAVED_QUOTATIONS_STATES, QuotationGroupSelector, SpinnerQuotation, StepContainer } from './containers';

import QuotationService from '../../../shared/services/quotation';

import { InfoContext } from '../../../shared/contexts';
import {
  QuotationInfoContext,
  QuotationInfoProvider,
} from './containers/shared/contexts/QuotationInfo';

import { Article, CurrencyCode, Group, SelectOption } from '../../../shared/interfaces';
import { QuotationGroups } from './containers/utils/quotation-groups.enum';

import { decrypt, displayErrorMessage, getError } from '../../../shared/utils';
import { ErrorResults } from '../../../shared/components';

import './index.css';
import { LoadQuotationToRenewResponse } from './containers/shared/interfaces/load-renewal-quotation-response-interface';

/** Ramas de cobertura de bien único: el backend (PDF y emisión) solo contempla un bien */
const SINGLE_ASSET_GROUPS: Array<string> = [
  QuotationGroups.ROBO,
  QuotationGroups.RESPONSABILIDAD_CIVIL,
  QuotationGroups.EFECTOS_PERSONALES,
];

/** Si una cotización guardada/renovación de bien único trae más de un bien, se conserva solo el primero */
const keepSingleAsset = (quotationData: any, group?: string) => {
  if (!group || !SINGLE_ASSET_GROUPS.includes(group) || !(quotationData?.bienes?.length > 1)) return;
  quotationData.bienes = quotationData.bienes.slice(0, 1);
  message.warning('Esta cotización tenía más de un bien. Este producto admite un único bien, se conservó el primero.');
};

export const NewQuoteContent = () => {

  let { quotation } = useParams();

  const {
    canShowConfirmIssue,
    currentStep,
    quotationInfo,
    setQuotationInfo,
    loadingSavedQuotation,
    setLoadingSavedQuotation,
    setSavedQuotation,
    quotationToRenewal,
    setQuotationToRenewal,
    loadSaveQuotationState,
    loadingRenewalQuotation,
    setLoadingRenewalQuotation,
    setLoadSaveQuotationState,
    savedQuotation,
    setAllArticleSelected,
  } = useContext(QuotationInfoContext);
  const { setTitle } = useContext(InfoContext);
  const { canShowSpinner, setCanShowSpinner } = useContext(GenericDataFormContext);

  const [groups, setGroups] = useState<Array<Group>>([]);
  const [groupSelected, setGroupSelected] = useState<string>();
  const [groupsList, setGroupsList] = useState<Array<SelectOption>>([]);
  const [isLoadingGroupsList, setIsLoadingGroupsList] = useState<boolean>(true);
  const [newQuotationNumber, setNewQuotationNumber] = useState<string>('');
  const [hasQuotationParam, setHasQuotationParam] = useState<boolean>(quotation !== undefined);
  const [buttonErrorType, setButtonErrorType] = useState<boolean>(true);
  const [isRenewalLimitsError, setIsRenewalLimitsError] = useState<boolean>(false);

  const navigate = useNavigate();

  const stepsItems = useMemo(
    () =>
      groupSelected !== QuotationGroups.INCENDIO &&
        groupSelected !== QuotationGroups.INTEGRAL_COMERCIO
        ? [
          { title: 'Datos del cliente' },
          { title: 'Datos del bien' },
          { title: 'Cotización' },
          { title: 'Emisión' },
        ]
        : [
          { title: 'Datos del cliente' },
          { title: 'Selección de rubro' },
          { title: 'Datos del bien' },
          { title: 'Cotización' },
          { title: 'Emisión' },
        ],
    [groupSelected]
  );

  const generateNewQuotation = async (
    article: string,
    branch: string,
    arse: string,
    group: string
  ) => {
    setGroupSelected(group);
    try {
      const response = await QuotationService.generateNewQuotation(article);

      if (response.data) {
        setNewQuotationNumber(response.data.numeroCotizacion);
        setQuotationInfo({
          arse,
          article,
          branch,
          token: response.data.numeroCotizacion,
          groupSelected: group,
        });
      }
    } catch (error) { }
  };

  const getGroupsOfArticles = useCallback(async () => {
    try {
      setCanShowSpinner(true);
      const response = await QuotationService.getArticleGroups();

      if (response.data?.Grupos?.length) {
        const groups: Array<SelectOption> = response.data?.Grupos?.map(
          ({ descripcion, codigo }) => ({ label: descripcion, value: codigo })
        );

        setGroups(response.data.Grupos);
        setGroupsList(groups);
        setIsLoadingGroupsList(false);
      }
    } catch (error) { } finally {
      setCanShowSpinner(false)
    }
  }, [
    setCanShowSpinner,
    setGroups,
    setGroupsList,
    setIsLoadingGroupsList,
  ]);

  const getArticleDescription = (articleCode: string) => {
    const articles = groups.find(
      (el) => el.codigo === groupSelected
    )?.articulos;
    const article = articles?.find((el) => el.codigo === articleCode);

    return article?.descLarga;
  };

  useEffect(() => {
    setTitle('Nueva Cotización');
    getGroupsOfArticles();
    /* eslint-disable-next-line */
  }, []);

  type RenewalService = (body: {
    nroDeSuperpolizaAfectada: string;
    articulo: string;
    monedaDeEmision: CurrencyCode;
  }) => Promise<LoadQuotationToRenewResponse>;
  
  const renewalServiceMap: Partial<Record<QuotationGroups, RenewalService>> = {
    [QuotationGroups.AUTOMOVILES]: QuotationService.postRenewalCarInformation,
    [QuotationGroups.INTEGRAL_COMERCIO]: QuotationService.postRenewalCommerceInformation,
    [QuotationGroups.INTEGRAL_CONSORCIO]: QuotationService.postRenewalConsortiumInformation,
    [QuotationGroups.HOGAR]: QuotationService.postRenewalFamilyInformation,
    [QuotationGroups.RESPONSABILIDAD_CIVIL]: QuotationService.postRenewalCRInformation,
    [QuotationGroups.EFECTOS_PERSONALES]: QuotationService.postRenewalPersonalEffectsInformation,
    [QuotationGroups.ROBO]: QuotationService.postRenewalTheftInformation,
    [QuotationGroups.INCENDIO]: QuotationService.postRenewalFireInformation,
  };

  const ID_ERROR_LIMITS_RENEWAL: string = 'COW0137';

  const loadQuotation = useCallback(async () => {
    const quotationInfo = decrypt<{ quotationNumber: string, groupSelected: QuotationGroups, article?: string, superQuotationNumber?: string, tipoOperacion?: string }>(quotation as string);    
    try {
      if (quotationInfo.article && quotationInfo.superQuotationNumber) {
        setTitle('Renovar Cotización');
        
        setButtonErrorType(false)
        let quotationToRenewal: LoadQuotationToRenewResponse;

        const body = {
          nroDeSuperpolizaAfectada: quotationInfo.superQuotationNumber,
          articulo: quotationInfo.article,
          monedaDeEmision: CurrencyCode.PESOS_ARGENTINOS
        }                
        
        const fetchRenewalInfo = renewalServiceMap[quotationInfo.groupSelected];
        
        if (fetchRenewalInfo) {
          try{
            quotationToRenewal = await fetchRenewalInfo(body);
          } catch (err: any) {
            if (typeof err?.message === "string" && err.message.toUpperCase().includes(ID_ERROR_LIMITS_RENEWAL)) {
              setIsRenewalLimitsError(true);
            }
          }          
        } 

        setQuotationInfo({
          arse: quotationToRenewal!.codigoArse,
          token: quotationToRenewal!.numero,
          branch: quotationToRenewal!.codigoRama,
          article: quotationToRenewal!.codigoArticulo,
          groupSelected: quotationInfo.groupSelected,
        });
        
        setGroupSelected(quotationInfo.groupSelected);
        keepSingleAsset(quotationToRenewal!, quotationInfo.groupSelected);
        setQuotationToRenewal(quotationToRenewal!);

        setLoadingRenewalQuotation(true);

      } else {

        const savedQuotation = await QuotationService.getSavedQuote(quotationInfo.quotationNumber);

        if (!savedQuotation?.data?.numero) {
          setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.HAS_ERROR);
          displayErrorMessage(`No fue posible recuperar la cotización ${quotationInfo?.quotationNumber}`);
          return;
        }

        if (quotationInfo.tipoOperacion) {
          savedQuotation.data.tipoOperacion = quotationInfo.tipoOperacion;
        }

        setQuotationInfo({
          arse: savedQuotation.data.codigoArse,
          token: savedQuotation.data.numero,
          branch: savedQuotation.data.codigoRama,
          article: savedQuotation.data.codigoArticulo,
          groupSelected: quotationInfo.groupSelected,
        });
        setGroupSelected(quotationInfo.groupSelected);
        keepSingleAsset(savedQuotation.data, quotationInfo.groupSelected);
        setSavedQuotation(savedQuotation.data);
        setLoadingSavedQuotation(true);
      }
    } catch (error) {
      setCanShowSpinner(false);
      setLoadSaveQuotationState(LOADING_SAVED_QUOTATIONS_STATES.HAS_ERROR);
      displayErrorMessage(`No fue posible recuperar la póliza N° ${quotationInfo?.quotationNumber}`)
    }
  }, [quotation, setQuotationInfo, setQuotationToRenewal, setLoadingRenewalQuotation, currentStep, setSavedQuotation, setLoadingSavedQuotation, setLoadSaveQuotationState, setCanShowSpinner]);

  useEffect(() => {
    if (!savedQuotation || !groups.length || !groupSelected) return;

    const group = groups.find((groupItem: Group) => String(groupItem.codigo) === String(groupSelected));
    const article = group?.articulos?.find((articleItem: Article) => String(articleItem.codigo) === String(savedQuotation.codigoArticulo));

    if (article) {
      setAllArticleSelected(article);
    }
  }, [savedQuotation, groups, groupSelected, setAllArticleSelected]);

  const errorActionButtons: Array<ActionButtonProps> = useMemo(() => [
    {
      label: 'Reintentar',
      onClick: () => window.location.reload(),
      className: 'btn-principal-outline-barbuss',
      type: 'secondary'
    },
    {
      label: buttonErrorType ? 'Ir a las Cotizaciones Guardadas' : 'Ir al listado de pólizas',
      onClick: () => navigate(buttonErrorType ? '/cotizador/cotizaciones-guardadas' : '/produccion/listado-de-polizas'),
    }
  ], [buttonErrorType])

  const errorActionButtonsErrorLimits: Array<ActionButtonProps> = useMemo(() => [
    {
      label: 'Ir al listado de pólizas',
      onClick: () => navigate('/produccion/listado-de-polizas'),
    }
  ], [])

  useEffect(() => {
    //Deberá validar el parámetro para determinar si se puede recuperar, 
    //en el caso de que no deberá setear hasQuotationParam como false...
    if (quotation && groups.length) {
      loadQuotation();
    } else {
      setHasQuotationParam(false);
    }
    /* eslint-disable-next-line */
  }, [groups]);

  useEffect(() => {
    if (loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.FINISH_LOADING) {
      setHasQuotationParam(false);
      setLoadingSavedQuotation(false);
      setLoadingRenewalQuotation(false);
    }
  }, [loadSaveQuotationState]);

  return (
    loadSaveQuotationState === LOADING_SAVED_QUOTATIONS_STATES.HAS_ERROR ? (
      isRenewalLimitsError ? (
        <ErrorResults
          title='Error al tratar de recuperar la cotización'
          message={
            <>
              La suma asegurada está fuera de los límites permitidos. Por favor comuníquese con <strong>polizasautos@barbuss.com</strong>
            </>
          }
          actions={errorActionButtonsErrorLimits}
        />
      ) : (
        <ErrorResults
          title='Error al tratar de recuperar la cotización'
          message={`Hubo un error al tratar de recuperar la póliza N° ${JSON.parse(atob(quotation as string))?.quotationNumber}, por favor inténtelo de nuevo más tarde`}
          actions={errorActionButtons}
        />
      )
    )
      : (
        <SpinnerQuotation spinnerProps={{ spinning: loadingRenewalQuotation || loadingSavedQuotation || canShowSpinner }}>
          <PageHeader />
          <>
            {quotationInfo ? (
              !canShowConfirmIssue && (
                <>
                  <Row>
                    <Col span={24}>
                      <Typography.Text strong style={{ fontSize: 16 }}>
                        Cotización Nro.: {newQuotationNumber || quotationInfo?.token || savedQuotation.token || quotationToRenewal.token}
                      </Typography.Text>
                    </Col>
                  </Row>
                  <Row style={{ marginBottom: 15 }}>
                    <Col span={24}>
                      <Typography.Text style={{ fontSize: 14 }}>
                        {getArticleDescription(quotationInfo?.article!)}
                      </Typography.Text>
                    </Col>
                  </Row>
                </>
              )
            ) : !hasQuotationParam && (
              <QuotationGroupSelector
                onInitQuotation={generateNewQuotation}
                groups={groups}
                groupsList={groupsList}
                isLoadingGroupsList={isLoadingGroupsList}
              />
            )}

            {quotationInfo && (
              <Row>
                <Col style={{ height: 'calc(100vh - 14rem)' }} span={20}>
                  <StepContainer />
                </Col>
                <Col style={{ paddingLeft: 15 }} span={4}>
                  {quotationInfo && !loadingSavedQuotation && (
                    <Wizard currentStep={currentStep} stepsItems={stepsItems} />
                  )}
                </Col>
              </Row>
            )}
          </>
        </SpinnerQuotation>
      )
  );
};

export const NewQuotePage = () => {
  return (
    <QuotationInfoProvider>

      <GenericDataFormProvider>
        <NewQuoteContent />
      </GenericDataFormProvider>
    </QuotationInfoProvider>
  );
};
